import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  InputAdornment, 
  Skeleton,
  Tabs,
  Tab, Chip,
  Pagination,
  Switch
} from '@mui/material';
import {  FaSearch as SearchIcon } from 'react-icons/fa';
import { TbCategoryPlus } from "react-icons/tb";
import { MdDeleteForever } from "react-icons/md";
import { postFileUpload, getData, deleteData, putData } from '../../Webservice/Webservice';
import Subcategories from '../../components/Categories/Subcategories';
import { FiX, FiType, FiUploadCloud, FiTrash2, FiCircle, FiSave } from 'react-icons/fi';
import CATEGORY_ROUTES from '../../API_Route/CategoryRoute';

const PAGE_SIZE = 20; // Default page size

function Categories() {
  // Categories and clusters state
  const [categories, setCategories] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [open, setOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({
    title: '',
    image: '',
    status: 'active',
    categoryCluster: ''
  });
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  const [filterCluster, setFilterCluster] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  
  // Pagination state (meta object)
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch categories with pagination
  const fetchCategories = async (page = 1) => {
    setLoading(true);
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      const params = {
        page,
        limit: PAGE_SIZE,
        ...(searchTerm && { search: searchTerm }),
        ...(filterCluster && { categoryCluster: filterCluster }),
        ...(filterStatus && { status: filterStatus })
      };
      const response = await getData(CATEGORY_ROUTES.GET_CATEGORY, params, token);
      setCategories(Array.isArray(response.categories) ? response.categories : []);
      // Use meta from response
      const meta = response.meta || { page: 1, totalPages: 1, total: 0 };
      setMeta({
        page: meta.page,
        totalPages: meta.totalPages,
        total: meta.total
      });
    } catch (error) {
      setCategories([]);
      setMeta({ page: 1, totalPages: 1, total: 0 });
      console.error('Fetch categories failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClusters = async () => {
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      const response = await getData(CATEGORY_ROUTES.LIST_CATEGORY_CLUSTER, { limit: 100 }, token);
      if (response && response.clusters) {
        setClusters(response.clusters);
      }
    } catch (error) {
      console.error('Fetch clusters failed:', error);
    }
  };

  useEffect(() => {
    fetchCategories(meta.page);
    fetchClusters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.page, searchTerm, filterCluster, filterStatus]);

  // Pagination handler
  const handlePageChange = (event, newPage) => {
    setMeta((prev) => ({ ...prev, page: newPage }));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleDeleteCategory = (category) => {
    setDeleteTarget({ type: 'category', value: category });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setDeleteTarget(null);
  };

  const handleConfirmDelete = async () => {
    if (deleteTarget && deleteTarget.type === 'category') {
      try {
        const tokenValue = sessionStorage.getItem('token');
        const token = tokenValue ? `Bearer ${tokenValue}` : '';
        const categoryToDelete = categories.find(cat => cat.title === deleteTarget.value);
        if (categoryToDelete) {
          await deleteData(`categories/delete/${categoryToDelete._id}`, token);
          fetchCategories(); // Refresh the list
        }
        handleClose();
      } catch (error) {
        console.error('Delete failed:', error);
      }
    }
  };

  const handleAddCategory = () => {
    setAddOpen(true);
  };

  const handleViewCategory = (category) => {
    setSelectedCategory(category);
    setViewOpen(true);
  };

  const handleAddCategoryClose = () => {
    setAddOpen(false);
    setNewCategory({ title: '', image: '', status: 'active', categoryCluster: '' });
    setImagePreview('');
  };

  const handleViewCategoryClose = () => {
    setViewOpen(false);
    setSelectedCategory(null);
  };

  const handleAddCategorySubmit = async () => {
    if (!newCategory.categoryCluster) {
      alert("Category Cluster is mandatory");
      return;
    }
    try {
      const formData = new FormData();
      formData.append('title', newCategory.title);
      formData.append('status', newCategory.status);
      if (newCategory.categoryCluster) {
        formData.append('categoryCluster', newCategory.categoryCluster);
      }
      if (newCategory.image) {
        formData.append('image', newCategory.image);
      }
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      await postFileUpload(CATEGORY_ROUTES.ADD_CATEGORY, formData, token);
      fetchCategories(); // Refresh the list
      handleAddCategoryClose();
    } catch (error) {
      console.error('Add category failed:', error);
    }
  };

  const handleStatusToggle = async (category) => {
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      const newStatus = category.status === 'active' ? 'inactive' : 'active';
      await putData(`categories/update/${category._id}`, { status: newStatus }, token);
      fetchCategories(meta.page); 
    } catch (error) {
      console.error('Toggle status failed:', error);
    }
  };

  const handleUpdateCategory = async () => {
    if (!selectedCategory || !selectedCategory._id) {
      console.error('No category ID found for update');
      return;
    }
    if (!selectedCategory.categoryCluster) {
      alert("Category Cluster is mandatory");
      return;
    }
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';

      // Check if there's a new image file
      const hasNewImage = selectedCategory.image && selectedCategory.image instanceof File;

      if (hasNewImage) {
        // Use postFileUpload for updates with images
        const formData = new FormData();
        formData.append('title', selectedCategory.title);
        formData.append('status', selectedCategory.status);
        if (selectedCategory.categoryCluster) {
          formData.append('categoryCluster', selectedCategory.categoryCluster._id || selectedCategory.categoryCluster);
        }
        formData.append('image', selectedCategory.image);
        await putData(`categories/update/${selectedCategory._id}`, formData, token);
      } else {
        // Use putData for text-only updates
        const updateData = {
          title: selectedCategory.title,
          status: selectedCategory.status,
          categoryCluster: selectedCategory.categoryCluster?._id || selectedCategory.categoryCluster || null
        };
        await putData(`categories/update/${selectedCategory._id}`, updateData, token);
      }

      fetchCategories(); // Refresh the list
      handleViewCategoryClose();
    } catch (error) {
      console.error('Update category failed:', error);
    }
  };

  return (
    <Box p={2} style={{ background: '#f8fafc', borderRadius: '10px' }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h3" component="h1" sx={{
          color: 'text.primary',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontFamily: 'Mulish, sans-serif',
        }}>
          Categories Management
          <Chip
            label={`${meta.total} ${meta.total === 1 ? 'Item' : 'Items'}`}
            size="small"
            sx={{ 
                backgroundColor: 'rgba(255, 209, 2, 0.2)', 
                color: '#b39200',
                fontFamily: 'Mulish, sans-serif', 
                fontWeight: '800',
                border: '1px solid rgba(255, 209, 2, 0.5)'
            }}
          />
        </Typography>
        
        {activeTab === 0 && (
          <Button
            variant="contained"
            startIcon={<TbCategoryPlus />}
            sx={{
              fontFamily: 'Mulish, sans-serif',
              background: 'rgb(255 209 2)',
              color: 'black',
              textTransform: 'none',
              borderRadius: '10px',
              fontWeight: 700,
              fontSize: '0.9rem',
              px: 3,
              boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)',
              border: '1px solid black',
              transition: 'all 0.2s ease',
              '&:hover': {
                background: 'rgb(255 209 2)',
                boxShadow: '0 6px 16px rgba(255, 209, 2, 0.4)',
                transform: 'translateY(-1px)'
              }
            }}
            onClick={handleAddCategory}
          >
            Add Category
          </Button>
        )}
      </Box>

      {/* Tabs */}
      <Box sx={{ 
    backgroundColor: 'action.hover',
    borderRadius: '8px',
    p: 0.5,
    mb: 1,
    display: 'inline-flex'
}}>
    <Tabs
        value={activeTab}
        onChange={handleTabChange}
        aria-label="categories and subcategories tabs"
        sx={{
            '& .MuiTabs-indicator': {
                display: 'none'
            }
        }}
    >
        <Tab 
            label="Categories" 
            sx={{ 
                fontWeight: 700, 
                textTransform: 'none',
                fontFamily: 'Mulish, sans-serif',
                borderRadius: '6px',
                mx: 0.5,
                py: 1,
                px: 3,
                color: activeTab === 0 ? 'black' : 'text.secondary',
                backgroundColor: activeTab === 0 ? 'rgb(255 209 2)' : 'transparent',
                '&.Mui-selected': {
                    color: 'black',
                    backgroundColor: 'rgb(255 209 2)'
                },
                '&:hover': {
                    backgroundColor: activeTab === 0 ? 'rgb(255 209 2)' : 'rgb(255 209 2 / 40%)'
                }
            }} 
        />
        <Tab 
            label="Subcategories" 
            sx={{ 
                fontWeight: 700, 
                textTransform: 'none',
                fontFamily: 'Mulish, sans-serif',
                borderRadius: '6px',
                mx: 0.5,
                py: 1,
                px: 3,
                color: activeTab === 1 ? 'black' : 'text.secondary',
                backgroundColor: activeTab === 1 ? 'rgb(255 209 2)' : 'transparent',
                '&.Mui-selected': {
                    color: 'black',
                    backgroundColor: 'rgb(255 209 2)'
                },
                '&:hover': {
                    backgroundColor: activeTab === 1 ? 'rgb(255 209 2)' : 'rgb(255 209 2 / 40%)'
                }
            }} 
        />
    </Tabs>
</Box>

      {/* Searchbar - Show only for Categories tab */}
      {activeTab === 0 && (
        <Box sx={{
          mb: 3,
          p: 2,
          backgroundColor: '#f8fafc',
          borderRadius: 2,
        }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search Categories..."
                size="small"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setMeta({ ...meta, page: 1 }); // Reset to first page when search changes
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2 }
                }}
              />
            </Grid>

            {/* Cluster Filter */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Cluster</InputLabel>
                <Select
                  value={filterCluster}
                  label="Cluster"
                  onChange={(e) => {
                    setFilterCluster(e.target.value);
                    setMeta({ ...meta, page: 1 });
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">All Clusters</MenuItem>
                  {clusters.map((cluster) => (
                    <MenuItem key={cluster._id} value={cluster._id}>
                      {cluster.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Status Filter */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Status"
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setMeta({ ...meta, page: 1 });
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchTerm('');
                  setFilterCluster('');
                  setFilterStatus('');
                  setMeta({ ...meta, page: 1 });
                }}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  borderColor: 'red',
                  color: 'red',
                  '&:hover': {
                    borderColor: 'darkred',
                    backgroundColor: 'rgba(255,0,0,0.1)'
                  }
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Tab Content */}
      {activeTab === 0 ? (
        <>
          <TableContainer component={Paper}
            elevation={0}
            sx={{
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border:'1px solid #d3d3d3de',
            }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{
                  '& th': {
                    backgroundColor: '#f8f9fa',
                    fontWeight: 800,
                    py: 2,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: '#5c636a',
                    fontFamily: 'Mulish, sans-serif',
                    borderBottom: '2px solid #edeff1',
                  }
                }}>
                  <TableCell>Title</TableCell>
                  <TableCell>Cluster</TableCell>
                  <TableCell>Image</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 6 }).map((_, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <Skeleton width="60%" />
                      </TableCell>
                      <TableCell>
                        <Skeleton width="40%" />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="rounded" width={40} height={40} />
                      </TableCell>
                      <TableCell>
                        <Skeleton width={100} height={24} />
                      </TableCell>
                      <TableCell>
                        <Skeleton width={80} />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  categories.length > 0 ? (
                    categories.map((cat) => (
                      <TableRow
                        key={cat._id}
                        hover
                        onClick={() => handleViewCategory(cat)}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#f9fafb' },
                          transition: 'background-color 0.2s ease'
                        }}
                      >
                        <TableCell>
                          <Typography 
                            fontWeight={700} 
                            sx={{ 
                              fontFamily:'Mulish, sans-serif',
                              fontSize: '0.95rem',
                              color: '#1a1c1e',
                              '&:hover': { color: '#0d47a1' }
                            }}
                          >
                            {cat.title}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'Mulish, sans-serif' }}>
                            {cat.categoryCluster?.name || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <img 
                            src={cat.image} 
                            alt={cat.title} 
                            style={{ 
                              width: 45, 
                              height: 45, 
                              borderRadius: '10px',
                              objectFit: 'cover',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                              border: '1px solid #e5e7eb'
                            }} 
                          />
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Switch 
                              checked={cat.status === 'active'}
                              size="small"
                              onChange={() => handleStatusToggle(cat)}
                              color="success"
                            />
                            <Chip
                              label={cat.status}
                              size="small"
                              sx={{
                                  fontFamily:'Mulish, sans-serif',
                                  fontWeight: 700,
                                  fontSize: '0.65rem',
                                  textTransform: 'uppercase',
                                  borderRadius: '6px',
                                  backgroundColor: cat.status === 'active' ? '#def7ec' : '#f3f4f6',
                                  color: cat.status === 'active' ? '#03543f' : '#4b5563',
                                  border: 'none'
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            sx={{ 
                                color: '#dc2626',
                                backgroundColor: 'rgba(220, 38, 38, 0.1)',
                                '&:hover': { backgroundColor: 'rgba(220, 38, 38, 0.2)' },
                                borderRadius: '8px'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCategory(cat.title);
                            }}
                          >
                            <MdDeleteForever size={20} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <img 
                            src="./img/nodata.png" 
                            alt="No categories" 
                            style={{ width: 150, marginBottom: 16 }} 
                          />
                          <Typography variant="h6" sx={{ mb: 1 }}>
                            No categories found
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {searchTerm ? 'No categories match your search' : 'Start by adding a new category'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Component (centered, like ActivityLog) */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            mt: 3,
            '& .MuiPagination-ul': {
              flexWrap: 'nowrap'
            }
          }}>
            <Pagination
              count={meta.totalPages}
              page={meta.page}
              onChange={handlePageChange}
              sx={{
                  '& .MuiPaginationItem-root': {
                      fontFamily: 'Mulish, sans-serif',
                      fontWeight: 600,
                      borderRadius: '8px',
                      '&.Mui-selected': {
                          backgroundColor: 'rgb(255, 209, 2)',
                          color: 'black',
                          '&:hover': {
                              backgroundColor: 'rgb(255, 190, 0)',
                          }
                      }
                  }
              }}
              shape="rounded"
              disabled={loading}
              showFirstButton
              showLastButton
            />
          </Box>
          {meta.total > 0 && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
              Showing {(meta.page - 1) * PAGE_SIZE + 1} to {Math.min(meta.page * PAGE_SIZE, meta.total)} of {meta.total} entries
            </Typography>
          )}
        </>
      ) : (
        <Subcategories categories={categories} />
      )}

      {/* Add Category Modal */}
      <Dialog
        open={addOpen}
        onClose={handleAddCategoryClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '1.25rem',
            fontWeight: 600,
            py: 2,
            px: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          Add New Category
          <IconButton
            onClick={handleAddCategoryClose}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.05)'
              }
            }}
          >
            <FiX size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ py: 3, px: 3 }}>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: '10PX' }}>
            {/* Title Field */}
            <TextField
              variant="outlined"
              label="Category Name"
              value={newCategory.title}
              onChange={e => setNewCategory({ ...newCategory, title: e.target.value })}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FiType color="#6c757d" size={18} />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: '8px',
                  '& input': {
                    py: 1.5
                  }
                }
              }}
            />

            {/* Cluster Selector */}
            <FormControl fullWidth required>
              <InputLabel>Category Cluster</InputLabel>
              <Select
                value={newCategory.categoryCluster}
                label="Category Cluster *"
                onChange={e => setNewCategory({ ...newCategory, categoryCluster: e.target.value })}
                variant="outlined"
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value="" disabled>
                  <em>Select Cluster</em>
                </MenuItem>
                {clusters.map((cluster) => (
                  <MenuItem key={cluster._id} value={cluster._id}>
                    {cluster.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Image Upload */}
            <Box>
              <Typography variant="subtitle2" sx={{
                mb: 1,
                fontWeight: 500,
                color: 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <FiUploadCloud size={16} />
                Category Image
              </Typography>

              <Box
                component="label"
                htmlFor="category-image-upload"
                sx={{
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(0, 123, 255, 0.05)'
                  }
                }}
              >
                {imagePreview ? (
                  <Box sx={{ position: 'relative', width: '100%' }}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        width: '100%',
                        maxHeight: 200,
                        objectFit: 'contain',
                        borderRadius: 6
                      }}
                    />
                    <IconButton
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.7)'
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setImagePreview('');
                        setNewCategory({ ...newCategory, image: '' });
                      }}
                    >
                      <FiTrash2 size={16} />
                    </IconButton>
                  </Box>
                ) : (
                  <>
                    <FiUploadCloud size={48} color="#adb5bd" style={{ marginBottom: '8px' }} />
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <Box component="span" sx={{ color: 'primary.main', fontWeight: 500 }}>
                        Click to upload
                      </Box> or drag and drop
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                      PNG, JPG, GIF (Max 2MB)
                    </Typography>
                  </>
                )}

                <input
                  id="category-image-upload"
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={e => {
                    const file = e.target.files[0];
                    if (file) {
                      setNewCategory({ ...newCategory, image: file });
                      setImagePreview(URL.createObjectURL(file));
                    }
                  }}
                />
              </Box>
            </Box>

            {/* Status Selector */}
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={newCategory.status}
                label="Status"
                onChange={e => setNewCategory({ ...newCategory, status: e.target.value })}
                variant="outlined"
                sx={{
                  borderRadius: '8px',
                  '& .MuiSelect-select': {
                    py: 1.5
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      marginTop: '4px'
                    }
                  }
                }}
              >
                <MenuItem value="active">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FiCircle size={14} color="#28a745" />
                    Active
                  </Box>
                </MenuItem>
                <MenuItem value="inactive">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FiCircle size={14} color="#6c757d" />
                    Inactive
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          px: 3,
          py: 2,
          gap: 2
        }}>
          <Button
            onClick={handleAddCategoryClose}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              px: 3,
              py: 1,
              border:'1px solid grey',
              color:"grey",
              fontWeight: 500,
              '&:hover': {
                borderWidth: '1.5px',
                backgroundColor: '#f8f9fa'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddCategorySubmit}
            disabled={!newCategory.title}
            sx={{
              border:"1px solid black",
              borderRadius: '8px',
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 500,
              boxShadow: 'none',
              background: 'rgb(255 209 2)',
              color: 'black',
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                background: 'rgb(255 209 2)'
              },
              '&:disabled': {
                background: '#e9ecef',
                color: '#adb5bd'
              }
            }}
          >
            Create Category
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Category Modal */}
      <Dialog
        open={viewOpen}
        onClose={handleViewCategoryClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '1.25rem',
            fontWeight: 600,
            py: 2,
            px: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          Edit Category
          <IconButton
            onClick={handleViewCategoryClose}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.05)'
              }
            }}
          >
            <FiX size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ py: 3, px: 3 }}>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 , marginTop:'10px'}}>
            {/* Title Field */}
            <TextField
              variant="outlined"
              label="Category Name"
              value={selectedCategory?.title || ''}
              onChange={e => setSelectedCategory({ ...selectedCategory, title: e.target.value })}
              fullWidth
            />

            {/* Cluster Selector */}
            <FormControl fullWidth required>
              <InputLabel>Category Cluster</InputLabel>
              <Select
                value={selectedCategory?.categoryCluster?._id || selectedCategory?.categoryCluster || ''}
                label="Category Cluster *"
                onChange={e => setSelectedCategory({ ...selectedCategory, categoryCluster: e.target.value })}
                variant="outlined"
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value="" disabled>
                  <em>Select Cluster</em>
                </MenuItem>
                {clusters.map((cluster) => (
                  <MenuItem key={cluster._id} value={cluster._id}>
                    {cluster.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Image Upload */}
            <Box>
              <Typography variant="subtitle2" sx={{
                mb: 1,
                fontWeight: 500,
                color: 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <FiUploadCloud size={16} />
                Category Image
              </Typography>

              <Box
                component="label"
                htmlFor="edit-category-image-upload"
                sx={{
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(0, 123, 255, 0.05)'
                  }
                }}
              >
                {selectedCategory?.image ? (
                  <Box sx={{ position: 'relative', width: '100%' }}>
                    <img
                      src={selectedCategory.image instanceof File ?
                        URL.createObjectURL(selectedCategory.image) :
                        selectedCategory.image}
                      alt="Preview"
                      style={{
                        width: '100%',
                        maxHeight: 200,
                        objectFit: 'contain',
                        borderRadius: 6
                      }}
                    />
                    <IconButton
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.7)'
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCategory({ ...selectedCategory, image: null });
                      }}
                    >
                      <FiTrash2 size={16} />
                    </IconButton>
                  </Box>
                ) : (
                  <>
                    <FiUploadCloud size={48} color="#adb5bd" style={{ marginBottom: '8px' }} />
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <Box component="span" sx={{ color: 'primary.main', fontWeight: 500 }}>
                        Click to upload
                      </Box> or drag and drop
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                      PNG, JPG, GIF (Max 2MB)
                    </Typography>
                  </>
                )}

                <input
                  id="edit-category-image-upload"
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={e => {
                    const file = e.target.files[0];
                    if (file) {
                      setSelectedCategory({ ...selectedCategory, image: file });
                    }
                  }}
                />
              </Box>
            </Box>

            {/* Status Selector */}
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={selectedCategory?.status || 'active'}
                label="Status"
                onChange={e => setSelectedCategory({ ...selectedCategory, status: e.target.value })}
                variant="outlined"
                sx={{
                  borderRadius: '8px',
                  '& .MuiSelect-select': {
                    py: 1.5
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      marginTop: '4px'
                    }
                  }
                }}
              >
                <MenuItem value="active">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FiCircle size={14} color="#28a745" />
                    Active
                  </Box>
                </MenuItem>
                <MenuItem value="inactive">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FiCircle size={14} color="#6c757d" />
                    Inactive
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          px: 3,
          py: 2,
          gap: 2
        }}>
          <Button
            onClick={handleViewCategoryClose}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              px: 3,
              py: 1,
              borderWidth: '1.5px',
              fontWeight: 500,
              borderColor: 'red',
                color: 'red',
                '&:hover': {
                  borderColor: 'darkred',
                backgroundColor: 'rgba(255,0,0,0.1)'
                }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateCategory}
            disabled={!selectedCategory?.title}
            startIcon={<FiSave size={18} />}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 500,
              boxShadow: 'none',
              background: 'rgb(255 209 2)',
              border: '1px solid black',
              fontFamily: 'Mulish, sans-serif',
              color: 'black',
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                background: 'rgb(255 209 2)'
              },
              '&:disabled': {
                background: '#e9ecef',
                color: '#adb5bd'
              }
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            borderRadius: '10px',
            padding: '20px',
            minWidth: '400px'
          }
        }}
      >
        <DialogTitle sx={{ fontSize: '20px', fontWeight: 'bold', textAlign: 'center' }}>
          <img style={{ width: '80px' }} src='./img/del1.png' alt='delete' />
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ textAlign: 'center', fontSize: '16px', mb: 1 }}>
            {deleteTarget && deleteTarget.type === 'category' && (
              <>Are you sure you want to delete category <b>{deleteTarget.value}</b> and all its subcategories?</>
            )}
          </DialogContentText>
          <Box sx={{
            textAlign: 'center',
            backgroundColor: '#fff3cd',
            padding: '12px',
            borderRadius: '4px',
            borderLeft: '4px solid #ffc107',
            marginBottom: '2px'
          }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#856404', marginBottom: '3px' }}>
              Warning
            </Typography>
            <Typography variant="body2" sx={{ color: '#856404' }}>
              By deleting, you won't be able to access it again.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: '20px', padding: '20px' }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              minWidth: '120px',
              borderColor: 'gray',
              color: 'gray',
              '&:hover': {
                borderColor: 'darkgray',
                backgroundColor: 'rgba(0,0,0,0.05)'
              }
            }}
          >
            No, Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              minWidth: '120px',
              backgroundColor: 'red',
              '&:hover': {
                backgroundColor: '#bb2d3b'
              }
            }}
            autoFocus
          >
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Categories;